/**
 * KYC Verification Required Dialog
 *
 * Modal that appears when users try to access restricted features
 * without completing KYC verification (phone + card verification).
 *
 * Features:
 * - Shows which verifications are missing
 * - Direct link to mobile app for KYC completion
 * - Dismissible but persistent until verified
 * - Role-aware theming
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../hooks/useTranslation';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import kycIcon from '../../../../assets/kyc.svg';

interface KycVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Custom message to display (optional) */
  customMessage?: string;
}

export const KycVerificationDialog: React.FC<KycVerificationDialogProps> = ({
  isOpen,
  onClose,
  customMessage,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Get primary color based on user role from theme
  const primaryColor = theme?.colors?.primary || '#9B1B5A';

  const handleCompleteKyc = () => {
    onClose();
    navigate('/update-profile?tab=kyc');
  };

  const handleMaybeLater = () => {
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kyc-verification-dialog-title"
      style={{
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={handleMaybeLater}
      />

      {/* Dialog Container */}
      <div
        className="relative flex flex-col items-center bg-white rounded-xl w-full max-w-[492px] overflow-hidden"
        style={{
          boxShadow: '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
          zIndex: 100000,
        }}
      >
        {/* Background Pattern - Decorative Circles */}
        <div className="absolute -left-[120px] -top-[120px] w-[336px] h-[336px] pointer-events-none">
          {/* Gradient Mask */}
          <div
            className="absolute w-[336px] h-[336px]"
            style={{
              background: 'radial-gradient(50% 50% at 50% 50%, #000000 0%, rgba(0, 0, 0, 0) 100%)',
              opacity: 0.02,
            }}
          />
          {/* Concentric Circles */}
          {[96, 144, 192, 240, 288, 336].map((size) => (
            <div
              key={size}
              className="absolute rounded-full border border-[#E9EAEB]"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}
          {/* KYC Icon in Center of Ripples */}
          <div
            className="absolute flex items-center justify-center w-12 h-12 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#F2E6EE',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* User head */}
              <circle
                cx="9"
                cy="7"
                r="3"
                stroke={primaryColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              {/* User body */}
              <path
                d="M2 21C2 17.134 5.134 14 9 14C10.5 14 11.9 14.5 13 15.3"
                stroke={primaryColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              {/* Checkmark */}
              <path
                d="M15.5 19L17.5 21L21.5 17"
                stroke={primaryColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </div>

        {/* Modal Header */}
        <div className="relative w-full flex flex-col items-start pt-24 pb-0 px-6 z-10">

          {/* Text Content */}
          <div className="w-full flex flex-col gap-2 mb-6">
            <h2
              id="kyc-verification-dialog-title"
              className="text-lg font-medium text-[#181D27]"
              style={{ fontFamily: 'Poppins', fontSize: '18px', lineHeight: '27px' }}
            >
              {t('kyc.required.title')}
            </h2>
            <p
              className="text-sm text-[#535862]"
              style={{ fontFamily: 'Poppins', fontSize: '14px', lineHeight: '21px' }}
            >
              {customMessage || t('kyc.required.message')}
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={handleMaybeLater}
            className="absolute top-4 right-4 flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close dialog"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#A4A7AE"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Modal Actions */}
        <div className="w-full flex flex-row gap-3 px-6 pb-6 pt-8">
          {/* Maybe Later Button */}
          <button
            onClick={handleMaybeLater}
            className="flex-1 flex items-center justify-center px-5 py-2.5 bg-white border border-[#D9DADF] rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '21px',
              color: '#484A54',
            }}
          >
            {t('kyc.action.later')}
          </button>

          {/* Complete KYC in App Button */}
          <button
            onClick={handleCompleteKyc}
            className="flex-1 flex items-center justify-center px-5 py-2.5 rounded-lg transition-colors"
            style={{
              backgroundColor: primaryColor,
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '21px',
              color: '#FFFFFF',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {t('common.kycnotification')}
          </button>
        </div>
      </div>
    </div>
  );
};
